import { Pressable } from 'react-native';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { FavouriteIcon as FavouriteStrokeRounded } from '@hugeicons-pro/core-stroke-rounded';
import { FavouriteIcon as FavouriteSolidRounded } from '@hugeicons-pro/core-solid-rounded';
import { Post } from '@/models/Post';
import { Comment } from '@/models/Comment';
import { Photo } from '@/models/Photo';
import { useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import Animated, {
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
} from 'react-native-reanimated';
import { useState, useEffect } from 'react';
import { plainToClass } from '@/models/transform';
import { PostsService } from '~/services/domain/PostsService';
import { CommentsService } from '~/services/domain/CommentsService';
import { PhotosService } from '~/services/domain/PhotosService';
import { useToastMessages } from '@/hooks/useToastMessages';

type ItemType = 'post' | 'comment' | 'photo';

interface LikeButtonProps {
  item: Post | Comment | Photo;
  itemType: ItemType;
  size?: number;
  iconClassName?: string;
  disabled?: boolean;
}

const createLikeUpdate = (item: Post | Comment | Photo, isLiked: boolean) => ({
  Id: item.Id,
  IsLiked: isLiked,
  LikesCount: isLiked ? item.LikesCount + 1 : Math.max(0, item.LikesCount - 1),
});

const optimisticUpdateCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  item: Post | Comment | Photo,
  itemType: ItemType,
  isLiked: boolean
) => {
  const itemUpdate = createLikeUpdate(item, isLiked);
  const queryKeyBase: QueryKey = [
    itemType === 'post' ? 'posts' : itemType === 'comment' ? 'comments' : 'photos'
  ];
  const itemsKey =
    itemType === 'post' ? 'PostItems' : itemType === 'comment' ? 'CommentItems' : 'PhotosItems';

  queryClient.setQueriesData({ queryKey: queryKeyBase }, (oldData: any) => {
    if (!oldData) return oldData;
    if (oldData.pages) {
      const newPages = oldData.pages.map((page: any) => {
        if (!page[itemsKey]?.some((i: Post | Comment | Photo) => i.Id === itemUpdate.Id)) {
          return page;
        }
        return {
          ...page,
          [itemsKey]: page[itemsKey].map((i: Post | Comment | Photo) => {
            if (i.Id === itemUpdate.Id) {
              if (itemType === 'post') {
                return plainToClass(Post, { ...i, ...itemUpdate });
              } else if (itemType === 'comment') {
                return plainToClass(Comment, { ...i, ...itemUpdate });
              } else {
                return plainToClass(Photo, { ...i, ...itemUpdate });
              }
            }
            return i;
          }),
        };
      });

      const updates: any = { ...oldData, pages: newPages };
      if (oldData.items) {
        updates.items = newPages.flatMap((page: any) => page[itemsKey]);
      }
      return updates;
    }
    return oldData;
  });

  // Also update the detail cache so consumers like ImageViewer stay in sync
  // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
  if (itemType === 'photo') {
    const photoId = itemUpdate.Id;
    queryClient.setQueriesData({ queryKey: ['photo', photoId] }, (oldData: Photo | undefined) => {
      if (!oldData) return oldData;
      return plainToClass(Photo, { ...oldData, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount });
    });

    // Cross-update posts caches where a post references this photo
    queryClient.setQueriesData({ queryKey: ['posts'] }, (oldData: any) => {
      if (!oldData?.pages) return oldData;
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        PostItems: page.PostItems?.map((p: Post) =>
          p?.PhotoId === photoId
            ? plainToClass(Post, { ...p, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount })
            : p
        ) ?? page.PostItems,
      }));
      const updates: any = { ...oldData, pages: newPages };
      if (oldData.items) {
        updates.items = newPages.flatMap((page: any) => page.PostItems ?? []);
      }
      return updates;
    });

    // Update any single post cache that references this photo
    queryClient.setQueriesData({ queryKey: ['post'] }, (oldData: any) => {
      if (!oldData || typeof oldData !== 'object') return oldData;
      if (typeof (oldData as any).Id === 'number' && (oldData as Post).PhotoId === photoId) {
        const post = oldData as Post;
        return plainToClass(Post, { ...post, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount });
      }
      return oldData;
    });
  }

  // Update the single post cache for this post
  if (itemType === 'post') {
    queryClient.setQueryData(['post', (item as Post).Id], (oldData: Post | undefined) => {
      const base = (oldData ?? (item as Post));
      return plainToClass(Post, { ...base, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount });
    });
  }

  // If liking/unliking a post that has a photo, keep the photo detail cache in sync too
  // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
  if (itemType === 'post' && (item as Post).PhotoId) {
    const photoId = (item as Post).PhotoId as number;
    queryClient.setQueriesData({ queryKey: ['photo', photoId] }, (oldData: Photo | undefined) => {
      if (!oldData) return oldData;
      return plainToClass(Photo, { ...oldData, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount });
    });

    // Optionally also update any photos list queries
    queryClient.setQueriesData({ queryKey: ['photos'] }, (oldData: any) => {
      if (!oldData?.pages) return oldData;
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        PhotosItems: page.PhotosItems?.map((ph: Photo) =>
          ph.Id === photoId ? plainToClass(Photo, { ...ph, IsLiked: itemUpdate.IsLiked, LikesCount: itemUpdate.LikesCount }) : ph
        ) ?? page.PhotosItems,
      }));
      const updates: any = { ...oldData, pages: newPages };
      if (oldData.items) {
        updates.items = newPages.flatMap((page: any) => page.PhotosItems ?? []);
      }
      return updates;
    });
  }
};

export function LikeButton({ item, itemType, size = 24, iconClassName, disabled = false }: LikeButtonProps) {
  const queryClient = useQueryClient();
  const scale = useSharedValue(1);
  const [isLiked, setIsLiked] = useState(item.IsLiked);
  const queryKeyBase: QueryKey = [
    itemType === 'post' ? 'posts' : itemType === 'comment' ? 'comments' : 'photos'
  ];
  const { showErrorToast } = useToastMessages();

  useEffect(() => {
    setIsLiked(item.IsLiked);
  }, [item.Id, item.IsLiked]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: scale.value }],
    };
  });

  const likeMutation = useMutation({
    mutationFn: () => {
      if (itemType === 'post') {
        return PostsService.likePost(item as Post);
      } else if (itemType === 'comment') {
        return CommentsService.likeComment(item as Comment);
      } else {
        return PhotosService.likePhoto((item as Photo).Id);
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeyBase });
      const previousData = queryClient.getQueriesData({ queryKey: queryKeyBase });
      optimisticUpdateCache(queryClient, item, itemType, true);
      return { previousData };
    },
    onSuccess: () => {
      // Invalidate member list query for likes to ensure MemberListSheet shows updated data
      if (itemType === 'post') {
        queryClient.invalidateQueries({ queryKey: ['members', 'post-likes', item.Id] });
      } else if (itemType === 'comment') {
        queryClient.invalidateQueries({ queryKey: ['members', 'comment-likes', item.Id] });
      } else if (itemType === 'photo') {
        queryClient.invalidateQueries({ queryKey: ['members', 'photo-likes', item.Id] });
      }
    },
    onError: (error, variables, context) => {
      setIsLiked(false);
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          if (queryKey[0] === queryKeyBase[0]) {
            queryClient.setQueryData(queryKey, data);
          }
        });
      }
      showErrorToast(error.message);
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: () => {
      if (itemType === 'post') {
        return PostsService.unlikePost(item as Post);
      } else if (itemType === 'comment') {
        return CommentsService.unlikeComment(item as Comment);
      } else {
        return PhotosService.unlikePhoto((item as Photo).Id);
      }
    },
    onMutate: async () => {
      setIsLiked(false);
      await queryClient.cancelQueries({ queryKey: queryKeyBase });
      const previousData = queryClient.getQueriesData({ queryKey: queryKeyBase });
      optimisticUpdateCache(queryClient, item, itemType, false);
      return { previousData };
    },
    onSuccess: () => {
      // Invalidate member list query for likes to ensure MemberListSheet shows updated data
      if (itemType === 'post') {
        queryClient.invalidateQueries({ queryKey: ['members', 'post-likes', item.Id] });
      } else if (itemType === 'comment') {
        queryClient.invalidateQueries({ queryKey: ['members', 'comment-likes', item.Id] });
      } else if (itemType === 'photo') {
        queryClient.invalidateQueries({ queryKey: ['members', 'photo-likes', item.Id] });
      }
    },
    onError: (error, variables, context) => {
      setIsLiked(true);
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          if (queryKey[0] === queryKeyBase[0]) {
            queryClient.setQueryData(queryKey, data);
          }
        });
      }
      showErrorToast(error.message);
    },
  });

  const handleLike = () => {
    if (disabled || likeMutation.isPending || unlikeMutation.isPending) return;

    cancelAnimation(scale);
    scale.value = 1;

    if (isLiked) {
      unlikeMutation.mutate();
    } else {
      setIsLiked(true);
      likeMutation.mutate();
      scale.value = withSequence(
        withSpring(1.3, { damping: 22, stiffness: 360, overshootClamping: true }),
        withSpring(1, { damping: 26, stiffness: 320, overshootClamping: true })
      );
    }
  };

  return (
    <Pressable onPress={handleLike} hitSlop={4} className="z-10">
      <Animated.View style={animatedStyle}>
        <HugeIconsWrapper
          icon={isLiked ? FavouriteSolidRounded : FavouriteStrokeRounded}
          size={size}
          className={isLiked ? 'text-red-500' : (iconClassName || 'text-typography-500')}
        />
      </Animated.View>
    </Pressable>
  );
}
